export default (node) => {
  const colorSelectButtons = node.querySelectorAll(
    '[data-color-select-button]'
  );
  const colorCategoryButtons = node.querySelectorAll(
    '[data-color-category-button]'
  );
  const colorCategoryResetButton = node.querySelector(
    '[data-color-category-reset-button]'
  );
  const colorSelect = node.querySelector('[data-color-selector]');
  const colorSelectOptions = node.querySelectorAll('[data-select-option]');
  const colorDescription = node.querySelector('[data-color-description]');

  // State for combined category + texture filtering
  let activeTextureFilter = null; // { texture, allVariants }
  let activeCategoryColors = null; // array of color names, or null for "All"

  const applyColorVisibility = () => {
    // First: update category button visibility based on texture filter
    if (activeTextureFilter) {
      const { texture, allVariants } = activeTextureFilter;
      let activeCategoryHidden = false;

      colorCategoryButtons.forEach((catButton) => {
        const { categoryColors } = catButton.dataset;
        const colors = categoryColors
          .split(',')
          .map((c) => c.replace('*', ','));

        const hasVisibleColors = colors.some((color) =>
          allVariants.some(
            (v) =>
              v.texture?.toUpperCase() === texture.toUpperCase() &&
              v.color?.toUpperCase() === color.toUpperCase()
          )
        );

        if (hasVisibleColors) {
          catButton.closest('li').classList.remove('hidden');
        } else {
          catButton.closest('li').classList.add('hidden');
          if (catButton.classList.contains('border-control-active')) {
            activeCategoryHidden = true;
          }
        }
      });

      // If the active category has no colors for this texture, reset to "All"
      if (activeCategoryHidden) {
        activeCategoryColors = null;
        colorCategoryButtons.forEach((btn) => {
          btn.classList.remove('border-control-active');
          btn.classList.add('border-transparent');
        });
        colorCategoryResetButton.classList.add('border-control-active');
        colorCategoryResetButton.classList.remove('border-transparent');
      }
    } else {
      // No texture filter — show all categories
      colorCategoryButtons.forEach((catButton) => {
        catButton.closest('li').classList.remove('hidden');
      });
    }

    // Then: apply combined filter for color buttons
    colorSelectButtons.forEach((button) => {
      const { color } = button.dataset;
      let visible = true;

      // Texture filter: hide colors that don't exist for the selected texture
      if (activeTextureFilter) {
        const { texture, allVariants } = activeTextureFilter;
        const exists = allVariants.some(
          (v) =>
            v.texture?.toUpperCase() === texture.toUpperCase() &&
            v.color?.toUpperCase() === color.toUpperCase()
        );
        if (!exists) visible = false;
      }

      // Category filter: hide colors not in the active category
      if (visible && activeCategoryColors) {
        if (!activeCategoryColors.includes(color)) {
          visible = false;
        }
      }

      if (visible) {
        button.classList.remove('hidden');

        // Update sold-out status based on texture availability
        if (activeTextureFilter) {
          const { texture, allVariants } = activeTextureFilter;
          const isAvailable = allVariants.some(
            (v) =>
              v.texture?.toUpperCase() === texture.toUpperCase() &&
              v.color?.toUpperCase() === color.toUpperCase() &&
              v.available
          );
          if (isAvailable) {
            button.classList.remove('color-select--sold-out');
          } else {
            button.classList.add('color-select--sold-out');
          }
        }
      } else {
        button.classList.add('hidden');
      }
    });

    // Also update dropdown options
    colorSelectOptions.forEach((option) => {
      const { color } = option.dataset;
      let visible = true;

      if (activeTextureFilter) {
        const { texture, allVariants } = activeTextureFilter;
        const exists = allVariants.some(
          (v) =>
            v.texture?.toUpperCase() === texture.toUpperCase() &&
            v.color?.toUpperCase() === color.toUpperCase()
        );
        if (!exists) visible = false;
      }

      if (visible && activeCategoryColors) {
        if (!activeCategoryColors.includes(color)) {
          visible = false;
        }
      }

      if (visible) {
        option.classList.remove('hidden');
      } else {
        option.classList.add('hidden');
      }
    });
  };

  /**
   * Functions
   */
  const changeSelectedColor = (button, scroll = true) => {
    const { variants, color } = button.dataset;
    const variantOptions = variants.split(',').map((variant) => {
      const options = variant.split('|');
      return {
        id: options[0],
        size: options[1],
        availability: options[2],
      };
    });

    // Dispatch event to scroll to the selected image and update the carousel
    const event = new CustomEvent('product-image:scroll-to-image', {
      detail: { option: color, scroll },
    });
    document.dispatchEvent(event);

    // Remove active class from all buttons
    colorSelectButtons.forEach((button) => {
      button.classList.remove('color-select--active');
    });
    // Add active class to the clicked button
    button.classList.add('color-select--active');

    // Update the color description
    updateColorDescription(color);
    // Set the selected option in the select dropdown
    colorSelect.value = variantOptions[0].id;

    // Update size buttons availability
    if (activeTextureFilter) {
      // 3-option product: filter by current texture so we don't use
      // availability from a different texture's variant
      const { texture, allVariants } = activeTextureFilter;
      const filteredOptions = allVariants
        .filter(
          (v) =>
            v.texture?.toUpperCase() === texture.toUpperCase() &&
            v.color?.toUpperCase() === color.toUpperCase()
        )
        .map((v) => ({
          id: String(v.id),
          size: v.size,
          availability: String(v.available),
        }));
      document.dispatchEvent(
        new CustomEvent('size-select:update-availability', {
          detail: { variantOptions: filteredOptions },
        })
      );
    } else {
      // 2-option product: use original color variant data
      document.dispatchEvent(
        new CustomEvent('size-select:update-availability', {
          detail: { variantOptions },
        })
      );
    }

    // Update add to cart button
    const eventUpdateButton = new CustomEvent(
      'add-to-cart:selected-color-change',
      {
        detail: { color: color },
      }
    );
    document.dispatchEvent(eventUpdateButton);

    // Update pair it with button
    document.dispatchEvent(new CustomEvent('pair-it-with:update-content'));
  };

  const updateColorDescription = (color) => {
    const colorHandle = color
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase() // Normalize the string to lowercase
      .replace(/['",]/g, '') // Remove single and double quotes
      .split(/[\s-&]+/) // Split by spaces, hyphens, or ampersands
      .map((word, index) =>
        index === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1)
      )
      .join('');

    const colorDescriptionText = colorDescription.dataset[colorHandle];
    colorDescription.innerHTML = colorDescriptionText;
  };

  const handleColorCategoryClick = (button) => {
    const { categoryColors } = button.dataset;
    activeCategoryColors = categoryColors
      .split(',')
      .map((color) => color.replace('*', ','));

    // Remove active class from all category buttons
    colorCategoryButtons.forEach((button) => {
      button.classList.remove('border-control-active');
      button.classList.add('border-transparent');
    });
    colorCategoryResetButton.classList.remove('border-control-active');
    colorCategoryResetButton.classList.add('border-transparent');

    // Add active class to the clicked button
    button.classList.add('border-control-active');
    button.classList.remove('border-transparent');

    // Apply combined category + texture filters
    applyColorVisibility();

    // Select the first visible color in the category
    for (let index = 0; index < colorSelectButtons.length; index++) {
      const btn = colorSelectButtons[index];
      if (!btn.classList.contains('hidden')) {
        changeSelectedColor(btn, false);
        break;
      }
    }
  };

  const handleColorCategoryReset = () => {
    activeCategoryColors = null;

    colorCategoryResetButton.classList.add('border-control-active');
    colorCategoryResetButton.classList.remove('border-transparent');

    colorCategoryButtons.forEach((button) => {
      button.classList.remove('border-control-active');
      button.classList.add('border-transparent');
    });

    // Re-apply visibility with category cleared (texture filter still applies)
    applyColorVisibility();
  };

  const handleSelectChange = (e) => {
    const { value } = e.target;
    for (let index = 0; index < colorSelectButtons.length; index++) {
      const button = colorSelectButtons[index];
      if (button.dataset.variants.includes(value)) {
        changeSelectedColor(button);
        break;
      }
    }
  };

  const currentColorDescription = () => {
    colorSelectButtons.forEach((button) => {
      if (button.classList.contains('color-select--active')) {
        updateColorDescription(button.dataset.color);
      }
    });
  };

  // 3-option: filter color visibility when texture changes
  // Uses shared applyColorVisibility to respect active category filter
  const filterByTexture = (e) => {
    const { texture, allVariants } = e.detail;
    activeTextureFilter = { texture, allVariants };

    applyColorVisibility();

    // If currently active color got hidden, auto-select the first visible color
    const activeButton = node.querySelector('.color-select--active');
    if (activeButton && activeButton.classList.contains('hidden')) {
      const firstVisible = Array.from(colorSelectButtons).find(
        (btn) => !btn.classList.contains('hidden')
      );
      if (firstVisible) {
        changeSelectedColor(firstVisible, false);
      }
    } else if (activeButton) {
      // Active color is still visible — update size availability
      // to reflect the new texture + current color combination
      const { color } = activeButton.dataset;
      const filteredOptions = allVariants
        .filter(
          (v) =>
            v.texture?.toUpperCase() === texture.toUpperCase() &&
            v.color?.toUpperCase() === color.toUpperCase()
        )
        .map((v) => ({
          id: String(v.id),
          size: v.size,
          availability: String(v.available),
        }));
      document.dispatchEvent(
        new CustomEvent('size-select:update-availability', {
          detail: { variantOptions: filteredOptions },
        })
      );
    }
  };

  // 3-option: update sold-out status based on texture + size
  const filterByTextureAndSize = (e) => {
    const { texture, size, allVariants } = e.detail;

    colorSelectButtons.forEach((button) => {
      // Skip hidden buttons (already filtered out by texture)
      if (button.classList.contains('hidden')) return;

      const { color } = button.dataset;
      const isAvailable = allVariants.some(
        (v) =>
          v.texture?.toUpperCase() === texture.toUpperCase() &&
          v.size?.toUpperCase() === size.toUpperCase() &&
          v.color?.toUpperCase() === color.toUpperCase() &&
          v.available
      );

      if (isAvailable) {
        button.classList.remove('color-select--sold-out');
      } else {
        button.classList.add('color-select--sold-out');
      }
    });
  };

  const init = () => {
    colorSelectButtons.forEach((button) => {
      button.addEventListener('click', () => changeSelectedColor(button));
    });

    colorCategoryButtons.forEach((button) => {
      button.addEventListener('click', () => handleColorCategoryClick(button));
    });

    colorCategoryResetButton.addEventListener(
      'click',
      handleColorCategoryReset
    );

    colorSelect.addEventListener('change', handleSelectChange);

    // show current color description
    currentColorDescription();

    // 3-option: filter color visibility by texture
    document.addEventListener(
      'color-select:filter-by-texture',
      filterByTexture
    );

    // 3-option: update color availability by texture+size
    document.addEventListener(
      'color-select:filter-by-texture-and-size',
      filterByTextureAndSize
    );

    // first load, get the current data-color-select-button and update the select data-color-selector
    const currentColorSelectButton = document.querySelector(
      '[data-color-select-button].color-select--active'
    );
    if (currentColorSelectButton && colorSelectOptions) {
      colorSelectOptions.forEach((option) => {
        if (option.dataset.color === currentColorSelectButton.dataset.color) {
          option.selected = true;
        }
      });
    }
  };

  /**
   * Init
   */
  init();
};
